import { ChangeEvent, useState } from 'react'

import { MultiSelectDropdown, Option, TextField } from '@joor/design-system'
import { useDispatch, useSelector } from 'react-redux'

import { JPG_AND_PNG, JPG_PNG_AND_SVG } from 'utils/files'

import { UPDATE_PROFILE } from 'store/storefront/constants'
import { getProfileInfo } from 'store/storefront/selectors'

import { Loader, Textarea } from 'components/Core'

import BrandDetailActions from './Actions/BrandDetailActions'
import { useProfileInfo } from './BrandDetails.hooks'
import * as S from './BrandDetails.styles'
import {
  PRICE_RANGE_ERROR_MESSAGE,
  RANGE_ERROR_HIGHLIGHT,
  blockNonDigitKey,
  blockNonDigitPaste,
  getErrorMessage,
  isPriceRangeInverted,
  toNonNegativeInput,
  validateSocialField,
} from './BrandDetails.utils'
import DragDropImage from './DragDropImg/DragDropImg'

const PROFILE_IMG: string = 'PROFILE_IMG'
const BRAND_LOGO: string = 'BRAND_LOGO'
const IPAD_PHOTO: string = 'IPAD_PHOTO'

export type Image = {
  id?: string
  url?: string
  mediaType?: string
  __typename?: string
}

export type ProductLine = {
  id: string
  name: string
  active: boolean
  __typename?: string
}

const BrandDetails: React.FC = () => {
  const dispatch = useDispatch()
  const profile = useSelector(getProfileInfo)
  const { loading, productLines } = useProfileInfo()
  const [isPictureResetValue, setResetPictureValue] = useState<boolean>(false)
  const [isLogoResetValue, setResetLogoValue] = useState<boolean>(false)
  const [isIpadPhotoResetValue, setResetIpadPhotoValue] =
    useState<boolean>(false)

  const selectImage = (
    id: string,
    image?: Image,
    resetValue: boolean = false,
  ): void => {
    if (id === PROFILE_IMG) {
      dispatch({
        type: UPDATE_PROFILE,
        value: { accountMedia: image },
      })
      setResetPictureValue(resetValue)
    } else if (id === BRAND_LOGO) {
      dispatch({
        type: UPDATE_PROFILE,
        value: { logo: image },
      })
      setResetLogoValue(resetValue)
    } else {
      dispatch({
        type: UPDATE_PROFILE,
        value: { ipadPhoto: image },
      })
      setResetIpadPhotoValue(resetValue)
    }
  }
  // The pair is what is wrong, so both ends turn red and the message appears
  // once under the row rather than twice, once beneath each input.
  const hasWholesaleRangeError = isPriceRangeInverted(
    profile?.wholesaleMin,
    profile?.wholesaleMax,
  )
  const hasRetailRangeError = isPriceRangeInverted(
    profile?.retailMin,
    profile?.retailMax,
  )

  /**
   * The year and the four price ends are all whole, non-negative numbers, so
   * every one of them filters the keystroke and the paste the same way.
   */
  const numberFieldProps = {
    type: 'number',
    min: 0,
    onKeyDown: blockNonDigitKey,
    onPaste: blockNonDigitPaste,
  }

  const categoriesOptions: Option[] = productLines.map((item: ProductLine) => {
    return { label: item.name, value: item.id }
  })
  const handleCategoriesSelection = (
    option: Option,
    action: string = 'add',
  ): void => {
    const selectedProductLineIds = profile?.productLineIds ?? []
    dispatch({
      type: UPDATE_PROFILE,
      value: {
        productLineIds:
          action === 'remove'
            ? selectedProductLineIds.filter(
                (productLineId: string) => productLineId !== option.value,
              )
            : selectedProductLineIds.concat(option.value),
      },
    })
  }

  return (
    <S.Container>
      <Loader active={loading} />
      <S.Title>Details</S.Title>
      <S.Info>
        Brand Details help retailers find and identify your profile across JOOR.
        Optionally, it can also be displayed on your profile via the “About Us”
        section.
      </S.Info>
      <S.Subtitle>General Info</S.Subtitle>
      <S.Block>
        <S.Label>Profile Picture</S.Label>
        <DragDropImage
          cropImage
          acceptedTypes={JPG_AND_PNG}
          alt="Profile Image"
          id={PROFILE_IMG}
          selectImage={selectImage}
          imgFile={profile?.accountMedia}
          messages={{
            requirements: 'JPG or PNG under 4MB',
            unsupported:
              'Unsupported file type. Please, upload a JPG or PNG image file',
            limit:
              'File too large. Please, upload a JPG or PNG image under 4MB.',
          }}
          shouldOpenModal={isPictureResetValue}
        />
      </S.Block>
      <S.Block>
        <S.Label>Brand Logo</S.Label>
        <DragDropImage
          acceptedTypes={JPG_PNG_AND_SVG}
          alt="Brand Logo"
          id={BRAND_LOGO}
          selectImage={selectImage}
          imgFile={profile?.logo}
          messages={{
            requirements: 'JPG, PNG or SVG under 4MB',
            unsupported:
              'Unsupported file type. Please, upload a JPG, PNG or SVG image file',
            limit:
              'File too large. Please, upload a JPG, PNG or SVG image under 4MB.',
          }}
          shouldOpenModal={isLogoResetValue}
        />
      </S.Block>
      <S.Block>
        <S.Label>iPad Photo</S.Label>
        <DragDropImage
          cropImage
          acceptedTypes={JPG_AND_PNG}
          alt="iPad Photo"
          id={IPAD_PHOTO}
          selectImage={selectImage}
          imgFile={profile?.ipadPhoto}
          messages={{
            requirements:
              'JPG or PNG under 4MB. Suggested dimensions for the photo: 1350 x 1406',
            unsupported:
              'Unsupported file type. Please, upload a JPG or PNG image file',
            limit:
              'File too large. Please, upload a JPG or PNG image under 4MB.',
          }}
          shouldOpenModal={isIpadPhotoResetValue}
        />
      </S.Block>
      <S.Block>
        <S.Label>Description</S.Label>
        <Textarea
          id="description"
          placeholder="Add your description here"
          value={profile?.description ?? ''}
          onChange={(e: Event) =>
            dispatch({
              type: UPDATE_PROFILE,
              value: {
                description: (e?.target as HTMLTextAreaElement).value,
              },
            })
          }
        />
      </S.Block>
      <S.Block>
        <MultiSelectDropdown
          options={categoriesOptions}
          label="Categories"
          value={categoriesOptions.filter((item: Option) =>
            (profile?.productLineIds ?? []).includes(item?.value),
          )}
          onSelect={({ selected }: { selected: Option }) =>
            handleCategoriesSelection(selected)
          }
          onDeselect={({ deselected }) =>
            handleCategoriesSelection(deselected, 'remove')
          }
        />
      </S.Block>
      <S.Block>
        <TextField
          {...numberFieldProps}
          id="yearEstablished"
          label="Year Established"
          value={profile?.yearEstablished ?? ''}
          onChange={(e: ChangeEvent) =>
            dispatch({
              type: UPDATE_PROFILE,
              value: {
                yearEstablished: toNonNegativeInput(
                  (e?.target as HTMLInputElement).value,
                ),
              },
            })
          }
        />
      </S.Block>
      <S.Block>
        <S.Label>Wholesale Price Range (USD)</S.Label>
        <S.PricesContainer>
          <TextField
            label="Minimum"
            {...numberFieldProps}
            id="wholesale_min"
            errorMessage={
              hasWholesaleRangeError ? RANGE_ERROR_HIGHLIGHT : undefined
            }
            value={profile?.wholesaleMin ?? ''}
            onChange={(e: ChangeEvent) => {
              dispatch({
                type: UPDATE_PROFILE,
                value: {
                  wholesaleMin: toNonNegativeInput(
                    (e?.target as HTMLInputElement).value,
                  ),
                },
              })
            }}
          />
          <span className="spacer">—</span>
          <TextField
            label="Maximum"
            {...numberFieldProps}
            id="wholesale_max"
            errorMessage={
              hasWholesaleRangeError ? RANGE_ERROR_HIGHLIGHT : undefined
            }
            value={profile?.wholesaleMax ?? ''}
            onChange={(e: ChangeEvent) =>
              dispatch({
                type: UPDATE_PROFILE,
                value: {
                  wholesaleMax: toNonNegativeInput(
                    (e?.target as HTMLInputElement).value,
                  ),
                },
              })
            }
          />
        </S.PricesContainer>
        {hasWholesaleRangeError && (
          <S.RangeError>{PRICE_RANGE_ERROR_MESSAGE}</S.RangeError>
        )}
      </S.Block>
      <S.Block>
        <S.Label>Retail Price Range (USD)</S.Label>
        <S.PricesContainer>
          <TextField
            label="Minimum"
            {...numberFieldProps}
            id="retail_min"
            errorMessage={
              hasRetailRangeError ? RANGE_ERROR_HIGHLIGHT : undefined
            }
            value={profile?.retailMin ?? ''}
            onChange={(e: ChangeEvent) =>
              dispatch({
                type: UPDATE_PROFILE,
                value: {
                  retailMin: toNonNegativeInput(
                    (e?.target as HTMLInputElement).value,
                  ),
                },
              })
            }
          />
          <span className="spacer"> — </span>
          <TextField
            label="Maximum"
            {...numberFieldProps}
            id="retail_max"
            errorMessage={
              hasRetailRangeError ? RANGE_ERROR_HIGHLIGHT : undefined
            }
            value={profile?.retailMax ?? ''}
            onChange={(e: ChangeEvent) =>
              dispatch({
                type: UPDATE_PROFILE,
                value: {
                  retailMax: toNonNegativeInput(
                    (e?.target as HTMLInputElement).value,
                  ),
                },
              })
            }
          />
        </S.PricesContainer>
        {hasRetailRangeError && (
          <S.RangeError>{PRICE_RANGE_ERROR_MESSAGE}</S.RangeError>
        )}
      </S.Block>
      <S.Subtitle>URLs</S.Subtitle>
      <S.Block>
        <TextField
          type="text"
          id="website_url"
          label="Website URL"
          value={profile?.websiteUrl ?? ''}
          onChange={(e: ChangeEvent) =>
            dispatch({
              type: UPDATE_PROFILE,
              value: { websiteUrl: (e?.target as HTMLInputElement).value },
            })
          }
        />
      </S.Block>
      <S.Block>
        <TextField
          type="text"
          id="joor_url"
          label="Custom JOOR URL"
          value={`https://www.jooraccess.com/${profile?.customUrlHandle}`}
          onChange={(e: ChangeEvent): void => {
            const rawURL =
              (e?.target as HTMLInputElement).value?.split(
                'https://www.jooraccess.com/',
              )?.[1] || ''
            dispatch({
              type: UPDATE_PROFILE,
              value: {
                customUrlHandle: rawURL,
              },
            })
          }}
        />
      </S.Block>
      <S.Subtitle>Social Links</S.Subtitle>
      <S.Block>
        <TextField
          id="facebook"
          label="Facebook (Profile Page URL)"
          placeholder="www.facebook.com/yourpage"
          value={profile?.facebookLink || ''}
          errorMessage={
            !profile?.facebookLink ||
            validateSocialField('facebook', profile.facebookLink)
              ? undefined
              : getErrorMessage('facebook')
          }
          onChange={(e: ChangeEvent): void => {
            const value = (e?.target as HTMLInputElement).value || ''
            dispatch({
              type: UPDATE_PROFILE,
              value: { facebookLink: value },
            })
          }}
        />
        <S.Block>
          <TextField
            id="instagram"
            label="Instagram Account Name"
            placeholder="@username"
            value={`@${profile?.instagramHandle || ''}`}
            errorMessage={
              !profile?.instagramHandle ||
              validateSocialField('instagram', profile.instagramHandle)
                ? undefined
                : getErrorMessage('instagram')
            }
            onChange={(e: ChangeEvent): void => {
              const raw = (e?.target as HTMLInputElement).value || ''
              const parts = raw.split('@')
              const afterFirstAt = parts.slice(1).join('@').replace(/@/g, '')
              const beforeFirstAt = (parts[0] || '').replace(/@/g, '')
              const value = afterFirstAt + beforeFirstAt
              dispatch({
                type: UPDATE_PROFILE,
                value: { instagramHandle: value },
              })
            }}
          />
        </S.Block>
        <S.Block>
          <TextField
            id="twitter"
            label="X Account Name"
            placeholder="@username"
            value={`@${profile?.twitterHandle || ''}`}
            errorMessage={
              !profile?.twitterHandle ||
              validateSocialField('twitter', profile.twitterHandle)
                ? undefined
                : getErrorMessage('twitter')
            }
            onChange={(e: ChangeEvent): void => {
              const raw = (e?.target as HTMLInputElement).value || ''
              const parts = raw.split('@')
              const afterFirstAt = parts.slice(1).join('@').replace(/@/g, '')
              const beforeFirstAt = (parts[0] || '').replace(/@/g, '')
              const value = afterFirstAt + beforeFirstAt
              dispatch({
                type: UPDATE_PROFILE,
                value: { twitterHandle: value },
              })
            }}
          />
        </S.Block>
      </S.Block>
      <BrandDetailActions />
    </S.Container>
  )
}

export default BrandDetails
